import {
  Component,
  Input,
  OnDestroy,
  OnInit,
  Output,
  EventEmitter,
} from '@angular/core';
import { BehaviorSubject, Subject, of } from 'rxjs';
import { switchMap, takeUntil, map, catchError } from 'rxjs/operators';
import { IdentityReportsService } from '@app/core/services/identity-reports.service';
import { Client } from '@app/core/types/client';
import {
  DOCUMENT_STATUS,
  ParsedDocumentStatus,
  parseDocumentStatus,
  formatDocumentReason,
} from '@app/core/types/id-verification';

export interface DocViewerSlot {
  /** Document ID to render, or null to show the empty state. */
  id: number | null;
}

@Component({
  selector: 'itfg-identity-document-viewer',
  templateUrl: './identity-document-viewer.component.html',
  styleUrl: './identity-document-viewer.component.scss',
  standalone: false,
})
export class IdentityDocumentViewerComponent implements OnInit, OnDestroy {

  @Input() document$!: BehaviorSubject<DocViewerSlot>;
  @Input() client!: Client;
  @Input() label = '';
  @Input() status = '';
  @Input() showSubmitButton = true;

  @Output() submitToVerification = new EventEmitter<number>();

  // ── State ─────────────────────────────────────────────────────────
  // imageUrl and prevImageUrl are kept separately so the old image can
  // remain visible (fading out) while the new one loads — eliminating
  // the blank-frame shift between selections.

  imageUrl: string | null = null;
  prevImageUrl: string | null = null;   // shown during load to prevent blank flash
  loading = false;
  currentDocId: number | null = null;
  error = false;
  zoomed = false;

  private _blobCache = new Map<number, Blob>();
  private _prevBlobUrl: string | null = null; // tracked separately for revocation
  private _unsubscribe = new Subject<void>();

  // ── Display config ─────────────────────────────────────────────────

  readonly STATUS_ICON: Record<DOCUMENT_STATUS, string> = {
    [DOCUMENT_STATUS.PENDING]: 'hourglass_empty',
    [DOCUMENT_STATUS.PROVIDED]: 'hourglass_empty',
    [DOCUMENT_STATUS.VALIDATING_SIMPLE]: 'hourglass_empty',
    [DOCUMENT_STATUS.VALIDATING_COMPLEX]: 'hourglass_empty',
    [DOCUMENT_STATUS.INVALID]: 'cancel',
    [DOCUMENT_STATUS.VALID]: 'check_circle',
  };

  readonly STATUS_CLASS: Record<DOCUMENT_STATUS, string> = {
    [DOCUMENT_STATUS.PENDING]: 'pending',
    [DOCUMENT_STATUS.PROVIDED]: 'provided',
    [DOCUMENT_STATUS.VALIDATING_SIMPLE]: 'validating_simple',
    [DOCUMENT_STATUS.VALIDATING_COMPLEX]: 'validating_complex',
    [DOCUMENT_STATUS.INVALID]: 'invalid',
    [DOCUMENT_STATUS.VALID]: 'valid',
  };

  constructor(private service: IdentityReportsService) { }

  // ── Derived status ─────────────────────────────────────────────────

  get parsedStatus(): ParsedDocumentStatus {
    return parseDocumentStatus(this.status);
  }

  /** CSS modifier class derived from the base status. */
  get statusClass(): string {
    return this.STATUS_CLASS[this.parsedStatus.base];
  }

  /** Icon name derived from the base status. */
  get statusIcon(): string {
    return this.STATUS_ICON[this.parsedStatus.base] ?? 'help_outline';
  }

  /**
   * Human-readable rejection reason.
   * Sourced from the status suffix (e.g. 'INVALID_PA_KATA' → 'Pa Kata').
   * Returns null when there is no reason to display.
   */
  get displayReason(): string | null {
    if (!this.parsedStatus.reason) {
      return null;
    }
    return formatDocumentReason(this.parsedStatus.reason);
  }

  // ── Lifecycle ──────────────────────────────────────────────────────

  ngOnInit(): void {
    this.document$
      .pipe(
        takeUntil(this._unsubscribe),
        switchMap(slot => {
          const incoming = slot?.id ?? null;
          this.error = false;

          // ── No document ──────────────────────────────────────────
          if (!incoming) {
            this._clearAll();
            return of<Blob | null>(null);
          }

          // ── Same doc already shown ───────────────────────────────
          if (incoming === this.currentDocId && this.imageUrl) {
            return of<Blob | null>(null);
          }

          // ── Switching to a different doc ─────────────────────────
          // Keep the old image visible as a ghost while the new one loads.
          this._setPrev(this.imageUrl);
          this._revokeImage();
          this.currentDocId = incoming;

          const cached = this._blobCache.get(incoming);
          if (cached) {
            // Instant — clear ghost immediately
            this._clearPrev();
            this.imageUrl = URL.createObjectURL(cached);
            this.loading = false;
            return of<Blob | null>(null);
          }

          this.loading = true;
          this.imageUrl = null;

          return this.service
            .renderIdentityDocumentContent(this.client.civilId, incoming)
            .pipe(
              map((raw: Blob) => {
                const blob = new Blob([raw as any]);
                this._blobCache.set(incoming, blob);
                return blob;
              }),
              catchError(err => {
                console.error('[IdentityDocumentViewer]', err);
                this.error = true;
                this._clearPrev();
                return of<Blob | null>(null);
              }),
            );
        }),
      )
      .subscribe({
        next: blob => {
          if (blob) {
            this.imageUrl = URL.createObjectURL(blob);
            this._clearPrev();
          }
          this.loading = false;
        },
      });
  }

  ngOnDestroy(): void {
    this._revokeImage();
    this._clearPrev();
    this._unsubscribe.next();
    this._unsubscribe.complete();
  }

  // ── Actions ────────────────────────────────────────────────────────

  onSubmit(): void {
    if (this.currentDocId != null) {
      this.submitToVerification.emit(this.currentDocId);
    }
  }

  // ── Private helpers ────────────────────────────────────────────────

  private _clearAll(): void {
    this._revokeImage();
    this._clearPrev();
    this.currentDocId = null;
    this.loading = false;
  }

  private _revokeImage(): void {
    if (this.imageUrl) {
      URL.revokeObjectURL(this.imageUrl);
      this.imageUrl = null;
    }
  }

  private _setPrev(url: string | null): void {
    this._clearPrev();
    this._prevBlobUrl = url;
    this.prevImageUrl = url;
  }

  private _clearPrev(): void {
    // Don't revoke — it might still be the cached blob object URL.
    // Just clear the reference so the ghost disappears.
    this._prevBlobUrl = null;
    this.prevImageUrl = null;
  }
}